import React from "react";
import { Play, Pause, Maximize2 } from "lucide-react";
import { VideoResult } from "../types";
import { motion } from "framer-motion";

interface VideoCardProps {
    video: VideoResult;
}

export const VideoCard: React.FC<VideoCardProps> = ({ video }) => {
    const videoRef = React.useRef<HTMLVideoElement | null>(null);
    const wrapperRef = React.useRef<HTMLDivElement | null>(null);
    const [playing, setPlaying] = React.useState(false);
    const [isFullscreen, setIsFullscreen] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);

    React.useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        const onPlay = () => setPlaying(true);
        const onPause = () => setPlaying(false);
        v.addEventListener("play", onPlay);
        v.addEventListener("pause", onPause);

        const onFullChange = () => {
            const fs = document.fullscreenElement === wrapperRef.current;
            setIsFullscreen(!!fs);
        };
        document.addEventListener("fullscreenchange", onFullChange);

        return () => {
            v.removeEventListener("play", onPlay);
            v.removeEventListener("pause", onPause);
            document.removeEventListener("fullscreenchange", onFullChange);
        };
    }, []);

    const togglePlay = async () => {
        const v = videoRef.current;
        if (!v) return;
        try {
            if (v.paused) {
                await v.play();
            } else {
                v.pause();
            }
        } catch (err) {
            console.warn("Video play failed", err);
        }
    };

    const goFullscreen = async () => {
        const wrapper = wrapperRef.current;
        const v = videoRef.current;
        if (!v) return;
        try {
            if (wrapper && (wrapper as any).requestFullscreen) {
                await (wrapper as any).requestFullscreen();
            } else if (v && (v as any).webkitEnterFullscreen) {
                (v as any).webkitEnterFullscreen();
            } else if (v && (v as any).requestFullscreen) {
                await (v as any).requestFullscreen();
            }
        } catch (err) {
            console.warn("Fullscreen request failed", err);
        }
    };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ y: -5 }}
            transition={{ duration: 0.2 }}
            className="group relative break-inside-avoid mb-6"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div
                ref={wrapperRef}
                className="relative w-full aspect-square bg-muted rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300"
            >
                <video
                    ref={videoRef}
                    className="video-thumb"
                    playsInline
                    preload="metadata"
                    controls={isFullscreen}
                    src={video.s3_url}
                    autoPlay
                    loop
                    muted
                />

                {/* Overlay Gradient */}
                <div
                    className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-opacity duration-300 ${
                        isHovered || playing ? "opacity-100" : "opacity-0"
                    }`}
                />

                {/* Controls */}
                <div
                    className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                        isHovered || playing ? "opacity-100" : "opacity-0"
                    }`}
                >
                    <button
                        aria-label={playing ? "Pause" : "Play"}
                        onClick={(e) => {
                            e.stopPropagation();
                            togglePlay();
                        }}
                        className="bg-white/20 hover:bg-white/40 backdrop-blur-md text-white p-4 rounded-full transform transition-transform hover:scale-110 active:scale-95"
                    >
                        {playing ? (
                            <Pause className="w-6 h-6 fill-current" />
                        ) : (
                            <Play className="w-6 h-6 fill-current" />
                        )}
                    </button>
                </div>

                <div
                    className={`absolute top-3 right-3 transition-opacity duration-300 ${
                        isHovered ? "opacity-100" : "opacity-0"
                    }`}
                >
                    <button
                        aria-label="Fullscreen"
                        onClick={(e) => {
                            e.stopPropagation();
                            goFullscreen();
                        }}
                        className="bg-black/40 hover:bg-black/60 backdrop-blur-sm text-white p-2 rounded-lg"
                    >
                        <Maximize2 className="w-4 h-4" />
                    </button>
                </div>

                {/* Info Overlay */}
                <div
                    className={`absolute bottom-0 left-0 right-0 p-4 transition-transform duration-300 ${
                        isHovered ? "translate-y-0" : "translate-y-2 opacity-0"
                    }`}
                >
                    <div className="flex items-end justify-end gap-2">
                        <div className="flex-shrink-0 bg-black/40 backdrop-blur-md px-2 py-1 rounded-md">
                            <span className="text-xs font-bold text-white">
                                {(video.score * 100).toFixed(0)}% Match
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};
